import React from 'react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useAppStore } from '@/lib/store';
import { isConvexEnabled } from '@/lib/convex';

export default function ContextModal() {
  const { contextModal, closeContextModal, userId } = useAppStore();
  const convexCreate = useMutation(api.contextFiles.create);

  if (!contextModal.isOpen) return null;

  const saveToConvex = async (item) => {
    if (!isConvexEnabled) return null;
    try {
      return await convexCreate({
        userId,
        name: item.name,
        type: item.type,
        content: item.content ?? undefined,
        url: item.url ?? undefined,
        wordCount: item.wordCount,
        charCount: item.charCount,
      });
    } catch (e) { console.error('Failed to save context file:', e); return null; }
  };

  const saveText = async () => {
    const name = document.getElementById('modal_file_name')?.value?.trim() || 'Untitled';
    const content = document.getElementById('modal_file_content')?.value || '';
    const item = {
      id: 'ctx_' + Date.now(), name, type: 'text',
      uploadDate: Date.now(), createdAt: Date.now(),
      wordCount: content.split(/\s+/).filter(Boolean).length,
      charCount: content.length,
      content, url: null, deletedCount: 0,
    };
    const _id = await saveToConvex(item);
    const { contextFiles, setContextFiles } = useAppStore.getState();
    setContextFiles([{ ...item, _id: _id ?? undefined, id: _id ?? item.id }, ...contextFiles]);
    closeContextModal();
  };

  const saveLink = async () => {
    const name = document.getElementById('modal_link_name')?.value?.trim();
    const url = document.getElementById('modal_link_url')?.value?.trim() || '';
    const item = {
      id: 'ctx_' + Date.now(), name: name || url, type: 'link',
      uploadDate: Date.now(), createdAt: Date.now(),
      wordCount: 0, charCount: 0, content: null, url, deletedCount: 0,
    };
    const _id = await saveToConvex(item);
    const { contextFiles, setContextFiles } = useAppStore.getState();
    setContextFiles([{ ...item, _id: _id ?? undefined, id: _id ?? item.id }, ...contextFiles]);
    closeContextModal();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#232426] border border-[#515153] rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {contextModal.mode === 'create_text' && (
          <>
            <div className="p-4 border-b border-[#515153] flex justify-between items-center">
              <h3 className="text-white font-bold text-[16px]">Create New Text File</h3>
              <button onClick={closeContextModal} className="text-[#8f9196] hover:text-white"><svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto flex flex-col gap-4">
              <input id="modal_file_name" type="text" placeholder="File name" className="bg-[#323335] border border-[#515153] rounded px-3 py-2 text-white text-[14px] outline-none focus:border-[#6498CC]" />
              <textarea id="modal_file_content" placeholder="Start typing..." className="bg-[#323335] border border-[#515153] rounded px-3 py-2 text-white text-[14px] outline-none focus:border-[#6498CC] flex-1 min-h-[200px] resize-none" />
            </div>
            <div className="p-4 border-t border-[#515153] flex justify-end gap-3">
              <button onClick={closeContextModal} className="px-4 py-2 text-[#d5d6d9] hover:bg-[#323335] rounded text-[13px] font-medium">Cancel</button>
              <button onClick={saveText} className="px-4 py-2 bg-[#6498CC] hover:bg-[#5282b8] text-white rounded text-[13px] font-bold">Save</button>
            </div>
          </>
        )}
        {contextModal.mode === 'create_link' && (
          <>
            <div className="p-4 border-b border-[#515153] flex justify-between items-center">
              <h3 className="text-white font-bold text-[16px]">Add URL Link</h3>
              <button onClick={closeContextModal} className="text-[#8f9196] hover:text-white"><svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
            </div>
            <div className="p-4 flex flex-col gap-4">
              <input id="modal_link_name" type="text" placeholder="Display Name (optional)" className="bg-[#323335] border border-[#515153] rounded px-3 py-2 text-white text-[14px] outline-none focus:border-[#6498CC]" />
              <input id="modal_link_url" type="url" placeholder="https://..." className="bg-[#323335] border border-[#515153] rounded px-3 py-2 text-white text-[14px] outline-none focus:border-[#6498CC]" />
            </div>
            <div className="p-4 border-t border-[#515153] flex justify-end gap-3">
              <button onClick={closeContextModal} className="px-4 py-2 text-[#d5d6d9] hover:bg-[#323335] rounded text-[13px] font-medium">Cancel</button>
              <button onClick={saveLink} className="px-4 py-2 bg-[#6498CC] hover:bg-[#5282b8] text-white rounded text-[13px] font-bold">Add Link</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
